import {
  BATTLESHIP_SIZE,
  battleshipShip,
  cellKey,
  cellLabel,
  hitCellsAgainst,
  placementCommitHash,
  randomPlacement,
  randomSalt,
  resolveShotAgainst,
  shipCells,
  validatePlacement,
  type BattleshipCell,
  type BattleshipMove,
  type BattleshipPlacement,
  type BattleshipShot,
  type BattleshipShipId,
  type BattleshipShipPlacement,
  type BattleshipState,
} from '@awwyeah/shared';

/**
 * Battleship's client-side decisions, kept pure and React-free so they can be unit tested
 * without a DOM (`apps/web`'s vitest is a node environment — see docs/DECISIONS.md
 * 2026-07-28/WS-4). `BattleshipGame.tsx` is then only rendering and Yjs writes.
 *
 * Three of these encode rules that matter more than the pixels do:
 *
 *  - `visiblePlacement` is the **only** place that decides whose ships a given viewer may
 *    see. It is written as one function precisely so "spectators see no ships until the
 *    reveal" is a rule with a name and a test, not an `&&` buried in JSX.
 *  - `pendingResolution` is the asynchronous-resolution loop: it is what makes a defender's
 *    open tab answer an incoming shot by itself, with nobody being asked to do anything.
 *  - `pendingReveal` publishes the placement (and its salt) once a game is over, on *any*
 *    finished game the viewer played in — including one they lost, and one they forfeited.
 *    It is what makes the end screen able to show both boards; the salt rides along as
 *    unexamined future-proofing (owner call 2026-08-04 — nothing verifies it today).
 */

export interface BattleshipSecret {
  placement: BattleshipPlacement;
  salt: string;
}

/** `-1` for a spectator. The rest of this file treats that as "sees only what's public". */
export type ViewerIndex = 0 | 1 | -1;

export function playerIndexOf(
  playerIds: readonly [string, string],
  userId: string | null | undefined,
): ViewerIndex {
  if (userId && playerIds[0] === userId) return 0;
  if (userId && playerIds[1] === userId) return 1;
  return -1;
}

export function otherIndex(index: 0 | 1): 0 | 1 {
  return index === 0 ? 1 : 0;
}

/**
 * Whose fleet may `viewer` actually see on `playerIndex`'s board, right now?
 *
 * Exactly three ways a fleet becomes visible, and no fourth:
 *  1. It's yours, and you hold the secret (read from your own per-user doc — nobody else can
 *     open it, enforced at the socket).
 *  2. The game is over and that player revealed, so the placement is *in the shared log* and
 *     public to everyone including spectators and anyone watching the replay later.
 *  3. Nothing. A spectator mid-game sees shots and results and no ships at all — they know
 *     only what's public, which is exactly what the players know about each other.
 */
export function visiblePlacement(
  state: BattleshipState,
  playerIndex: 0 | 1,
  viewer: ViewerIndex,
  mySecret: BattleshipSecret | null,
): BattleshipPlacement | null {
  const revealed = state.reveals[playerIndex];
  if (revealed) return revealed.placement;
  if (viewer === playerIndex && mySecret) return mySecret.placement;
  return null;
}

export interface BattleshipCellView {
  cell: BattleshipCell;
  /** The ship on this cell — only ever set when `visiblePlacement` allowed it. */
  shipId: BattleshipShipId | null;
  /** Whether that ship has been sunk (so the board can show a wreck rather than a hull). */
  shipSunk: boolean;
  /** What happened here, from the public log. `'pending'` is a fired-but-unanswered shot. */
  shot: 'hit' | 'miss' | 'pending' | null;
}

/**
 * One player's grid as the given viewer may see it: incoming shots always (public), ships
 * only when `placement` was handed in. `defenderIndex` owns the grid; the shots on it are
 * the *other* player's.
 */
export function boardView(
  state: BattleshipState,
  defenderIndex: 0 | 1,
  placement: BattleshipPlacement | null,
): BattleshipCellView[][] {
  const shipByCell = new Map<string, BattleshipShipId>();
  for (const ship of placement?.ships ?? []) {
    for (const cell of shipCells(ship)) shipByCell.set(cellKey(cell), ship.shipId);
  }
  const sunk = new Set(state.sunkByShooter[otherIndex(defenderIndex)]);
  const shotByCell = new Map<string, 'hit' | 'miss' | 'pending'>();
  for (const shot of state.shotsByShooter[otherIndex(defenderIndex)]) {
    shotByCell.set(cellKey(shot.cell), shot.result ?? 'pending');
  }

  return Array.from({ length: BATTLESHIP_SIZE }, (_, row) =>
    Array.from({ length: BATTLESHIP_SIZE }, (_, col) => {
      const cell = { row, col };
      const key = cellKey(cell);
      const shipId = shipByCell.get(key) ?? null;
      return {
        cell,
        shipId,
        shipSunk: shipId !== null && sunk.has(shipId),
        shot: shotByCell.get(key) ?? null,
      };
    }),
  );
}

/** The placement grid's view: a fleet-in-progress with no shots on it yet. Same cell shape
 * the battle boards use, so `Grid` renders both without a second code path. */
export function placementView(ships: readonly BattleshipShipPlacement[]): BattleshipCellView[][] {
  const shipByCell = new Map<string, BattleshipShipId>();
  for (const ship of ships) {
    for (const cell of shipCells(ship)) shipByCell.set(cellKey(cell), ship.shipId);
  }
  return Array.from({ length: BATTLESHIP_SIZE }, (_, row) =>
    Array.from({ length: BATTLESHIP_SIZE }, (_, col) => ({
      cell: { row, col },
      shipId: shipByCell.get(cellKey({ row, col })) ?? null,
      shipSunk: false,
      shot: null,
    })),
  );
}

/** The cells the currently-selected ship occupies — what the placement grid outlines. */
export function selectedShipCells(
  ships: readonly BattleshipShipPlacement[],
  shipId: BattleshipShipId,
): Set<string> {
  const ship = ships.find((s) => s.shipId === shipId);
  return new Set(ship ? shipCells(ship).map(cellKey) : []);
}

/** May this viewer fire at this cell right now? The client pre-check; `deriveBattleship` is
 * still the authority and re-validates on every client. */
export function canFire(
  state: BattleshipState,
  viewer: ViewerIndex,
  cell: BattleshipCell,
): boolean {
  if (viewer === -1) return false;
  if (state.turn !== viewer) return false;
  if (state.awaitingResolution) return false;
  return !state.shotsByShooter[viewer].some((shot) => cellKey(shot.cell) === cellKey(cell));
}

/**
 * The `resolve` move this viewer's device owes the game, or `null`. Called on every render
 * of an open board: if a shot is sitting unanswered and *we* are the defender and we can
 * read our own placement, we answer it — no prompt, no notification, no request that anyone
 * do anything. This is the whole of the "resolution happens automatically whenever the
 * defender has the app open" promise (`packages/shared/src/modules/games/battleship.ts`).
 *
 * Returns `null` for everyone else, including the defender's *opponent* (who couldn't
 * compute the answer anyway) and spectators.
 */
export function pendingResolution(
  state: BattleshipState,
  viewer: ViewerIndex,
  mySecret: BattleshipSecret | null,
  playerId: string,
): BattleshipMove | null {
  const pending = state.awaitingResolution;
  if (!pending || viewer === -1 || pending.defenderIndex !== viewer || !mySecret) return null;
  if (!validatePlacement(mySecret.placement).ok) return null;

  const answer = resolveShotAgainst(
    mySecret.placement,
    pending.cell,
    hitCellsAgainst(state, viewer),
  );
  return {
    type: 'resolve',
    playerId,
    shotIndex: pending.shotIndex,
    result: answer.result,
    ...(answer.sunkShipId ? { sunkShipId: answer.sunkShipId } : {}),
  };
}

/**
 * The `reveal` move this viewer owes, or `null`. Fires once the game is over — win, loss or
 * forfeit alike — so the end screen can show both boards. Nothing forces it: a client that
 * never opens the app again simply never reveals, and that board stays blank rather than
 * the screen pretending otherwise.
 */
export function pendingReveal(
  state: BattleshipState,
  viewer: ViewerIndex,
  mySecret: BattleshipSecret | null,
  playerId: string,
): BattleshipMove | null {
  if (state.phase !== 'done' || viewer === -1 || !mySecret) return null;
  if (state.reveals[viewer]) return null;
  return {
    type: 'reveal',
    playerId,
    placement: mySecret.placement,
    salt: mySecret.salt,
  };
}

/** A fresh secret for the placement phase — random fleet, random salt, and the commit hash
 * that goes in the shared doc. */
export function newSecret(rand: () => number = Math.random): BattleshipSecret {
  return { placement: randomPlacement(rand), salt: randomSalt(rand) };
}

export function commitMoveFor(secret: BattleshipSecret, playerId: string): BattleshipMove {
  return {
    type: 'commit',
    playerId,
    hash: placementCommitHash(secret.placement, secret.salt),
  };
}

/* -------------------------------------------------------------------------- */
/* Placement editing                                                           */
/* -------------------------------------------------------------------------- */

/**
 * Tap-to-place, not HTML5 drag. Touch drag is the single most fragile interaction on a
 * phone (it fights the scroller, it has no hover to preview with, and it needs a fallback
 * anyway), and the honest truth from the brief is that most people press Shuffle. So:
 * tap a ship, tap where its bow goes, tap it again to rotate. Every target is a full grid
 * cell or a 44px chip.
 */
export function placeShip(
  ships: readonly BattleshipShipPlacement[],
  shipId: BattleshipShipId,
  cell: BattleshipCell,
  orientation: 'h' | 'v',
): BattleshipShipPlacement[] | null {
  const candidate: BattleshipShipPlacement = { shipId, ...cell, orientation };
  const others = ships.filter((s) => s.shipId !== shipId);
  const next = [...others, candidate];
  return validatePlacement({ ships: next }).ok ? next : null;
}

/** Flips a placed ship in place, or returns `null` when it wouldn't fit rotated. */
export function rotateShip(
  ships: readonly BattleshipShipPlacement[],
  shipId: BattleshipShipId,
): BattleshipShipPlacement[] | null {
  const ship = ships.find((s) => s.shipId === shipId);
  if (!ship) return null;
  return placeShip(
    ships,
    shipId,
    { row: ship.row, col: ship.col },
    ship.orientation === 'h' ? 'v' : 'h',
  );
}

/**
 * The Rotate button's actual behaviour: flip in place, and if the flip alone doesn't fit
 * (an edge, another hull in the way), try the eight cells immediately around the current
 * bow before giving up. A one-cell nudge reads as "it turned, and slid over" rather than a
 * flat "no" — the UI still has to handle a real rejection (an edge-locked five-length ship
 * with no room to swing), but this makes that the rare case instead of the common one.
 * Returns `null` only when nothing nearby works, which the caller renders as a reject-wiggle.
 */
export function rotateShipNudged(
  ships: readonly BattleshipShipPlacement[],
  shipId: BattleshipShipId,
): BattleshipShipPlacement[] | null {
  const ship = ships.find((s) => s.shipId === shipId);
  if (!ship) return null;
  const nextOrientation = ship.orientation === 'h' ? 'v' : 'h';
  const offsets = [
    { dr: 0, dc: 0 },
    { dr: -1, dc: 0 },
    { dr: 1, dc: 0 },
    { dr: 0, dc: -1 },
    { dr: 0, dc: 1 },
    { dr: -1, dc: -1 },
    { dr: -1, dc: 1 },
    { dr: 1, dc: -1 },
    { dr: 1, dc: 1 },
  ];
  for (const { dr, dc } of offsets) {
    const row = ship.row + dr;
    const col = ship.col + dc;
    if (row < 0 || col < 0 || row >= BATTLESHIP_SIZE || col >= BATTLESHIP_SIZE) continue;
    const next = placeShip(ships, shipId, { row, col }, nextOrientation);
    if (next) return next;
  }
  return null;
}

/* -------------------------------------------------------------------------- */
/* Copy                                                                        */
/* -------------------------------------------------------------------------- */

/** The one line under the title. Facts only — never an instruction, never a prod. */
export function battleshipStatusLine(
  state: BattleshipState,
  viewer: ViewerIndex,
  names: [string, string],
): string {
  if (state.phase === 'done') {
    const winner = state.winner;
    if (winner === null) return 'Over';
    const byForfeit = state.winReason === 'forfeit';
    if (viewer === -1)
      return `${names[winner]} won${byForfeit ? ' — the other side resigned' : ''}`;
    if (viewer === winner) return byForfeit ? 'You won — they resigned' : 'You won';
    return byForfeit ? 'You resigned' : `${names[winner]} won`;
  }

  if (state.phase === 'placing') {
    const placed = [state.commits[0] !== null, state.commits[1] !== null];
    if (viewer === -1) {
      const waiting = placed[0] ? names[1] : placed[1] ? names[0] : 'both';
      return placed[0] && placed[1] ? 'Setting up' : `Waiting for ${waiting} to place`;
    }
    if (!placed[viewer]) return 'Place your fleet';
    return `Waiting for ${names[otherIndex(viewer)]} to place their fleet`;
  }

  const pending = state.awaitingResolution;
  if (pending) {
    if (viewer === pending.shooterIndex) {
      return `Shot fired at ${cellLabel(pending.cell)} — waiting for the seas to answer`;
    }
    if (viewer === pending.defenderIndex) return 'Incoming fire — resolving';
    return `${names[pending.shooterIndex]} fired — awaiting the answer`;
  }

  if (state.turn === null) return '';
  if (viewer === -1) return `${names[state.turn]}'s shot`;
  return state.turn === viewer ? 'Your shot' : `${names[state.turn]}'s shot`;
}

/**
 * The end screen's one line about the fleets. Says only what is true and visible: whether
 * both boards are on show. There is deliberately **no** fair-play verdict here (owner call,
 * 2026-08-04) — the commit hashes and salted reveals are in the log, but nothing re-hashes
 * them and nothing claims a game was "verified". A tick nobody earned is worse than no tick.
 */
export function revealNote(state: BattleshipState, names: [string, string]): string | null {
  if (state.phase !== 'done') return null;
  const missing = ([0, 1] as const).filter((i) => state.reveals[i] === null);
  if (missing.length === 0) return 'Both fleets shown';
  if (missing.length === 2) return 'Fleets stay hidden until both boards are opened again';
  return `${names[missing[0] as 0 | 1]}'s fleet appears once they open the game again`;
}

/** "You sank their Cruiser" / "They sank your Cruiser", newest last — a quiet running fact. */
export function sunkLog(
  state: BattleshipState,
  viewer: ViewerIndex,
  names: [string, string],
): string[] {
  const lines: string[] = [];
  for (const shot of state.shots) {
    if (!shot.sunkShipId) continue;
    const name = battleshipShip(shot.sunkShipId)?.name ?? 'ship';
    const shooter = shot.shooterIndex;
    if (viewer === -1) {
      lines.push(`${names[shooter]} sank ${names[otherIndex(shooter)]}'s ${name}`);
    } else if (viewer === shooter) {
      lines.push(`You sank their ${name}`);
    } else {
      lines.push(`They sank your ${name}`);
    }
  }
  return lines;
}

/** One shot's moment on screen: the shell going out, or the sea answering. */
export interface ShotFx {
  defender: 0 | 1;
  row: number;
  col: number;
  kind: 'fire' | 'hit' | 'miss' | 'sunk';
  /** Bumps on every new effect so the same cell can animate twice. */
  seq: number;
}

/** What changed between two shot lists: exactly one new shot, or exactly one shot answered.
 * Anything bigger (opening the game, jumping in history) is a snap, not a moment. */
export function shotChange(
  prev: readonly BattleshipShot[],
  next: readonly BattleshipShot[],
): { shot: BattleshipShot; kind: ShotFx['kind'] } | null {
  const kindOf = (s: BattleshipShot): ShotFx['kind'] =>
    s.result === null ? 'fire' : s.sunkShipId ? 'sunk' : s.result;
  if (next.length === prev.length + 1) {
    const shot = next[next.length - 1];
    return shot ? { shot, kind: kindOf(shot) } : null;
  }
  if (next.length !== prev.length) return null;
  const answered = next.filter((s, i) => prev[i]?.result === null && s.result !== null);
  const shot = answered.length === 1 ? answered[0] : undefined;
  return shot ? { shot, kind: kindOf(shot) } : null;
}
